// Frontend/src/pages/resident/ResidentOtpVerifyPage.tsx
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  persistResidentAuthenticatedState,
  persistResidentSelectionState,
  requestResidentClaimOtp,
  requestResidentLoginOtp,
  verifyResidentLoginOtp,
} from '@/api/residentAuth';
import { queryClient } from '@/lib/queryClient';
import logoImg from '@/assets/logo.png';
import { useLanguage } from "@/contexts/LanguageContext";

const PRIMARY = "#1F3A93";
const ACCENT = "#27AE60";
const BG = "#F5F6FA";

/**
 * Mask an email address for display
 * e.g., "maria.gonzalez@email.com" -> "m***a.g***z@e***l.com"
 */
function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return '***@***.com';
  
  const [localPart, domain] = email.split('@');
  
  // Mask local part: show first and last char
  let maskedLocal: string;
  if (localPart.length <= 2) {
    maskedLocal = localPart[0] + '***';
  } else {
    maskedLocal = localPart[0] + '***' + localPart[localPart.length - 1];
  }
  
  // Mask domain: show first char and TLD
  const domainParts = domain.split('.');
  const tld = domainParts.pop() || 'com';
  const domainName = domainParts.join('.');
  let maskedDomain: string;
  if (domainName.length <= 2) {
    maskedDomain = domainName[0] + '***.' + tld;
  } else {
    maskedDomain = domainName[0] + '***.' + tld;
  }
  
  return `${maskedLocal}@${maskedDomain}`;
}

export default function ResidentOtpVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Get email and optional resident_id from URL
  const emailFromUrl = searchParams.get('email') || '';
  const residentIdFromUrl = searchParams.get('resident_id') || '';
  const maskedEmailDisplay = maskEmail(emailFromUrl);
  
  const [code, setCode] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [isResending, setIsResending] = React.useState(false);

  // If no email in URL, show error message
  const hasValidEmail = emailFromUrl && emailFromUrl.includes('@');

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  function applyServerCooldown(resendAllowedAt: string | null | undefined) {
    if (!resendAllowedAt) {
      setResendCooldown(300);
      return;
    }
    const remaining = Math.ceil((new Date(resendAllowedAt).getTime() - Date.now()) / 1000);
    setResendCooldown(Math.max(remaining, 0));
  }

  async function handleResendOtp() {
    if (!emailFromUrl || isResending) return;
    setIsResending(true);
    setError(null);
    try {
      const response = residentIdFromUrl
        ? await requestResidentClaimOtp({ resident_id: residentIdFromUrl, email: emailFromUrl })
        : await requestResidentLoginOtp({ email: emailFromUrl });
      if (response.resend_allowed_at) {
        applyServerCooldown(response.resend_allowed_at);
      } else {
        setResendCooldown(300);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.cooldown_active && data?.resend_allowed_at) {
        applyServerCooldown(data.resend_allowed_at);
        setError(t("resident.otp.cooldownError"));
      } else {
        setError(data?.message || t("resident.otp.loginFailed"));
      }
    } finally {
      setIsResending(false);
    }
  }

  function hexToRgba(hex: string, alpha = 1) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!emailFromUrl || !code) {
      setError(t("resident.otp.enterCodeError"));
      return;
    }

    setIsLoading(true);

    try {
      const payload: any = { email: emailFromUrl, code };
      if (residentIdFromUrl) {
        payload.resident_id = residentIdFromUrl;
      }
      const response = await verifyResidentLoginOtp(payload);

      if (response.success) {
        // Clear any previously cached data from another user's session
        queryClient.clear();

        if (response.requires_property_selection) {
          persistResidentSelectionState(response);
          navigate('/resident/select-property');
        } else {
          persistResidentAuthenticatedState(response);
          if (response.has_completed_setup) {
            navigate('/resident');  // Go to dashboard if already set up
          } else {
            navigate('/resident/setup');  // Go to setup if first time
          }
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t("resident.otp.loginFailed");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  // Show different content if no email provided
  if (!hasValidEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
        <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
          {/* Logo & Title */}
          <div className="flex flex-col items-center mb-6">
            <img
              src={logoImg}
              alt="Urbe360 logo"
              width={124}
              height={124}
              className="mb-2"
            />
            <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
          </div>

          <h1 className="text-center text-2xl font-semibold mb-4 text-gray-900">
            {t("resident.otp.requiredTitle")}
          </h1>
          
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 mb-6">
            <p className="text-sm text-amber-800 text-center">
              <strong>{t("resident.otp.noEmailTitle")}</strong><br /><br />
              {t("resident.otp.noEmailDesc")}
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-gray-600 hover:text-gray-900 underline underline-offset-4"
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={logoImg}
            alt="Urbe360 logo"
            width={124}
            height={124}
            className="mb-2"
          />
          <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <h1 className="text-center text-3xl font-semibold mb-2 text-gray-900">
          {t("resident.otp.title")}
        </h1>
        
        {/* Email notification - shows masked email */}
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 mb-6">
          <p className="text-sm text-green-800 text-center">
            <span className="font-medium">{t("resident.otp.sentTo")}</span><br />
            <span className="font-mono text-base">{maskedEmailDisplay}</span>
          </p>
        </div>

        <p className="text-center text-sm text-gray-600 mb-6">
          {t("resident.otp.subtitle")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* OTP Code */}
          <div className="space-y-2">
            <label htmlFor="code" className="block text-sm text-gray-800 text-center">
              {t("resident.otp.enterCode")}
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={isLoading}
              placeholder={t("resident.otp.placeholder")}
              maxLength={6}
              autoFocus
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 text-center text-2xl font-bold tracking-widest outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PRIMARY;
                e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(ACCENT, 0.14)}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
            <p className="text-xs text-gray-500 text-center">
              {t("resident.otp.checkEmail")}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="group inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all active:scale-[0.99] disabled:opacity-60"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
          >
            {isLoading ? t("common.verifying") : t("activation.verifyLogin")}
            <ArrowRightCircleIcon className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
          </button>
        </form>

        {/* Help text */}
        <div className="mt-6 text-center space-y-3">
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{t("resident.otp.didntReceive")}</p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || isResending}
              className="text-sm font-medium underline underline-offset-4 disabled:opacity-50 disabled:no-underline"
              style={{ color: resendCooldown > 0 ? '#9ca3af' : PRIMARY }}
            >
              {isResending
                ? t("common.sending")
                : resendCooldown > 0
                ? `${t("resident.otp.resendIn")} ${resendCooldown}s`
                : t("resident.otp.resendCode")}
            </button>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-gray-600 hover:text-gray-900 underline underline-offset-4"
          >
            {t("auth.backToLogin")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Arrow icon */
function ArrowRightCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" opacity="0.85" />
      <path d="M10 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
